import React, { useCallback, useEffect, useState } from "react";
import { Wand2, Info, Home } from "lucide-react";

import { getPriceIndication } from "../api/prospectingReportsApi";

const TEAL = "#55c5d0";
const MAX_COMMENT = 600;

const money = (n) => (n == null || n === "" ? "" : `$${Number(n).toLocaleString("en-AU")}`);

/**
 * Label for the suggest button, e.g.
 *   "Use $1,395,000 - $1,650,000 from your 3 comparables"
 *   "Use $1,395,000 from your 1 comparable"
 * A single comparable produces min === max, so printing it as a range reads as a
 * mistake. Pluralisation matters too: this sits in front of vendors' agents.
 */
const suggestionLabel = ({ min, max, count }) => {
  const range = min === max ? money(min) : `${money(min)} - ${money(max)}`;
  return `Use ${range} from your ${count} comparable${count === 1 ? "" : "s"}`;
};

/**
 * Step 4 — Appraise price.
 *
 * Ignite offers an automatic PropTrack range and a manual override. We have no
 * automated valuation model, so the "suggest" button derives a range from the
 * comparables the agent selected — which is what a real appraisal does anyway,
 * and is defensible in front of a vendor because they can see the sales it came
 * from. The figure is always the agent's to set.
 */
const CmaAppraiseStep = ({ report, form, onChange, comparables }) => {
  const [suggestion, setSuggestion] = useState(null);

  const picked = comparables || [];
  const sold = picked.filter((c) => c.kind === "sold");
  const soldPrices = sold.filter((c) => c.price).map((c) => Number(c.price));

  // A suggested SALE range can only come from actual sales. Asking prices are
  // what a seller hopes for, not what the market paid, so they are excluded.
  // Spell out which of those two situations the agent is in, because "select
  // some sold comparables" alone does not explain why the button is missing
  // when they have already selected several.
  const whyNoSuggestion = () => {
    if (picked.length === 0) {
      return "Select some sold comparables on step 1 and we can suggest a range.";
    }
    if (sold.length === 0) {
      return `Your ${picked.length} selected comparable${picked.length === 1 ? " is an" : "s are"} `
        + "on-market or rental listing" + (picked.length === 1 ? "" : "s") + ", not sales. "
        + "Add at least one from the Sold tab on step 1 and we can suggest a range.";
    }
    return `Your ${sold.length} sold comparable${sold.length === 1 ? " has" : "s have"} no `
      + "sold price recorded, so there is nothing to average. Add one with a price, or set the "
      + "range yourself below.";
  };

  const loadSuggestion = useCallback(async () => {
    if (soldPrices.length === 0) { setSuggestion(null); return; }
    try {
      const { data } = await getPriceIndication(soldPrices);
      setSuggestion(data);
    } catch (error) {
      setSuggestion(null);
    }
    // Recompute only when the set of sold prices actually changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [soldPrices.join(",")]);

  useEffect(() => { loadSuggestion(); }, [loadSuggestion]);

  const set = (key, value) => onChange({ ...form, [key]: value });

  const applySuggestion = () => {
    if (!suggestion?.min) return;
    onChange({
      ...form,
      sale_estimate_mode: "manual",
      sale_estimate_min: suggestion.min,
      sale_estimate_max: suggestion.max,
    });
  };

  const comment = form.appraisal_comment || "";
  const remaining = MAX_COMMENT - comment.length;

  return (
    <div className="grid md:grid-cols-3 gap-6">
      <div className="md:col-span-2 space-y-4">
        {/* Sale estimate */}
        <div className="border border-gray-200 rounded-xl overflow-hidden">
          <label className="flex items-center gap-2 px-4 py-3 border-b border-gray-100 cursor-pointer">
            <input
              type="checkbox"
              checked={form.sale_estimate_mode === "manual"}
              onChange={(e) => set("sale_estimate_mode", e.target.checked ? "manual" : "none")}
              className="w-4 h-4 accent-[#55c5d0]"
            />
            <span className="text-sm font-bold text-gray-900">Sale Estimate</span>
          </label>

          <div className="p-4">
            {soldPrices.length === 0 ? (
              <div className="flex items-start gap-2 text-xs text-gray-600 bg-[#f0fbfc] border border-[#bfe9ee] rounded-lg p-3 mb-3">
                <Info className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: TEAL }} />
                <p>{whyNoSuggestion()}</p>
              </div>
            ) : (
              <button
                type="button"
                onClick={applySuggestion}
                disabled={!suggestion?.min}
                className="inline-flex items-center gap-1.5 mb-3 px-3 py-1.5 rounded-full border border-gray-200 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                <Wand2 className="w-3.5 h-3.5" />
                {suggestion?.min ? suggestionLabel(suggestion) : "Suggest from comparables"}
              </button>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">From</label>
                <input
                  type="number"
                  min="0"
                  step="1000"
                  value={form.sale_estimate_min ?? ""}
                  onChange={(e) => set("sale_estimate_min", e.target.value === "" ? null : Number(e.target.value))}
                  disabled={form.sale_estimate_mode !== "manual"}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm disabled:bg-gray-50"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">To</label>
                <input
                  type="number"
                  min="0"
                  step="1000"
                  value={form.sale_estimate_max ?? ""}
                  onChange={(e) => set("sale_estimate_max", e.target.value === "" ? null : Number(e.target.value))}
                  disabled={form.sale_estimate_mode !== "manual"}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm disabled:bg-gray-50"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Rental estimate */}
        <div className="border border-gray-200 rounded-xl overflow-hidden">
          <label className="flex items-center gap-2 px-4 py-3 border-b border-gray-100 cursor-pointer">
            <input
              type="checkbox"
              checked={form.rental_estimate_mode === "manual"}
              onChange={(e) => set("rental_estimate_mode", e.target.checked ? "manual" : "none")}
              className="w-4 h-4 accent-[#55c5d0]"
            />
            <span className="text-sm font-bold text-gray-900">Rental Estimate</span>
          </label>

          <div className="p-4 grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">From (weekly)</label>
              <input
                type="number"
                min="0"
                step="10"
                value={form.rental_estimate_min ?? ""}
                onChange={(e) => set("rental_estimate_min", e.target.value === "" ? null : Number(e.target.value))}
                disabled={form.rental_estimate_mode !== "manual"}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm disabled:bg-gray-50"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">To (weekly)</label>
              <input
                type="number"
                min="0"
                step="10"
                value={form.rental_estimate_max ?? ""}
                onChange={(e) => set("rental_estimate_max", e.target.value === "" ? null : Number(e.target.value))}
                disabled={form.rental_estimate_mode !== "manual"}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm disabled:bg-gray-50"
              />
            </div>
          </div>
        </div>

        {/* Note about estimates — the same disclaimer Ignite carries, because a
            written estimate to a vendor needs one. */}
        <div className="text-[11px] text-gray-500 leading-relaxed">
          <p className="font-semibold text-gray-600 mb-1">A note about estimates</p>
          <p>
            Sale and rental estimates are a guide only and are not a sworn valuation. They may not
            represent the market value of the property, and are not a representation that it will
            sell or lease within the stated range.
          </p>
        </div>

        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">Appraisal comment</label>
          <textarea
            rows={5}
            maxLength={MAX_COMMENT}
            value={comment}
            onChange={(e) => set("appraisal_comment", e.target.value)}
            placeholder="What the vendor should take from these numbers."
            className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#55c5d0]/40"
          />
          <p className={`text-[11px] mt-1 ${remaining < 50 ? "text-amber-600" : "text-gray-400"}`}>
            {remaining} characters available.
          </p>
        </div>
      </div>

      {/* Preview */}
      <div className="md:col-span-1">
        <p className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-2">Preview</p>
        <div className="border border-gray-200 rounded-xl p-4">
          <div className="w-full h-24 rounded-lg bg-gray-100 mb-3 overflow-hidden flex items-center justify-center">
            {report?.image_url
              ? <img src={report.image_url} alt="" className="w-full h-full object-cover" />
              : <Home className="w-6 h-6 text-gray-300" />}
          </div>
          <p className="text-sm font-bold text-gray-900">{report?.full_address}</p>
          <p className="text-xs text-gray-500 mb-3">
            {[report?.suburb, report?.state, report?.postcode].filter(Boolean).join(" ")}
          </p>
          <p className="text-xs text-gray-600">
            {[
              report?.bedrooms != null && `${report.bedrooms} bed`,
              report?.bathrooms != null && `${report.bathrooms} bath`,
              report?.car_spaces != null && `${report.car_spaces} car`,
              report?.land_size && `${Math.round(report.land_size)}m²`,
            ].filter(Boolean).join(" · ")}
          </p>

          {form.sale_estimate_mode === "manual" && form.sale_estimate_min != null && (
            <div className="mt-3 pt-3 border-t border-gray-100">
              <p className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold">
                Estimated sale range
              </p>
              <p className="text-lg font-bold text-gray-900">
                {money(form.sale_estimate_min)}
                {form.sale_estimate_max ? ` – ${money(form.sale_estimate_max)}` : ""}
              </p>
            </div>
          )}

          <div className="mt-3 pt-3 border-t border-gray-100 text-xs text-gray-500">
            {(comparables || []).length} comparable{(comparables || []).length === 1 ? "" : "s"} selected
          </div>
        </div>
      </div>
    </div>
  );
};

export default CmaAppraiseStep;
